import {
  useApolloClient,
  useLazyQuery,
  useMutation,
} from "@apollo/client/react";
import { useCallback } from "react";
import ReactGA from "react-ga4";
import { LOGIN, ME, UPDATE_CURRENT_USER_PROFILE } from "@/hooks";
import { useAuth, useUser } from "@/app/providers";
import { fillUserWithMe, isAbortedRequestError } from "@/utils";

type LoginCredentials = {
  email: string;
  password: string;
  rememberMe: boolean;
  captcha?: string;
};

type UpdateProfileVariables = Record<string, unknown>;

/**
 * Custom hook to handle user login.
 * @param {Function} [onCompleted] - Callback function when login succeeds.
 * @param {Function} [onError] - Callback function when login fails.
 * @returns {{submit: Function, isSubmitting: boolean}}
 */
export function useLogin(
  onCompleted: (data?: any) => void = () => {},
  onError: (error?: any) => void = () => {},
) {
  const { setToken } = useAuth();
  const { setUser } = useUser();
  const apolloClient = useApolloClient();

  const [getMe, { loading: getting }] = useLazyQuery(ME, {});
  const [comLogin, { loading: submitting }] = useMutation<any>(LOGIN);
  const [comUpdateAccount] = useMutation<any>(UPDATE_CURRENT_USER_PROFILE);

  const updateProfile = useCallback(
    async (variables: UpdateProfileVariables) => {
      try {
        await comUpdateAccount({
          variables,
          refetchQueries: [{ query: ME }],
        });
      } catch {
        // Silently fail
      }
    },
    [comUpdateAccount],
  );

  const submit = useCallback(
    (credentials: LoginCredentials) => {
      try {
        comLogin({
          variables: credentials,
        })
          .then((data) => {
            const loginResult = data?.data?.login ?? data?.data?.comLogin ?? {};
            if (loginResult?.success) {
              if (loginResult?.token) {
                setToken(loginResult.token);

                let count = 0;
                const maxTries = 30;
                // Keep trying 30 seconds if login successful.
                const intervalId = setInterval(() => {
                  getMe()
                    .then((data) => {
                      if (count++ > maxTries) {
                        clearInterval(intervalId);
                      }
                      let meData = {};
                      if (data?.data) {
                        meData = data?.data;
                      }

                      // @ts-ignore
                      // @ts-expect-error
                      if (meData?.me) {
                        clearInterval(intervalId);
                        setUser({ ...(meData as any).me });
                        fillUserWithMe(
                          (meData as any).me,
                          apolloClient,
                          setUser,
                          updateProfile,
                          true,
                        )
                          .then((finalMe) => {
                            setUser({ ...finalMe });
                          })
                          .catch(() => {
                            // Still set user even if fillUserWithMe fails
                            setUser({ ...(meData as any).me });
                          });
                      }
                      onCompleted(meData);
                    })
                    .catch((error: unknown) => {
                      if (isAbortedRequestError(error)) {
                        // Apollo client recreation during token transition can abort in-flight queries; ignore and let retry succeed
                        return;
                      }
                      clearInterval(intervalId);
                      onError(
                        error instanceof Error ? error.message : String(error),
                      );
                    });
                }, 1000);
                // });
              }
            } else {
              onError(loginResult?.message?.[0] ?? "Login failed");
            }
          })
          .catch((error: unknown) => {
            if (isAbortedRequestError(error)) {
              return;
            }
            onError(error instanceof Error ? error.message : String(error));
          });

        ReactGA.event({
          category: "Authentication",
          action: "Login",
          label: credentials?.email.replace("@", "[at]"),
        });
      } catch (err: unknown) {
        onError(err instanceof Error ? err.message : String(err));
      }
    },
    [
      comLogin,
      getMe,
      onCompleted,
      onError,
      setToken,
      setUser,
      apolloClient,
      updateProfile,
    ],
  );

  return {
    submit,
    isSubmitting: submitting || getting,
  };
}
